import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { ArrowLeft, Sparkles, Undo2, CheckCircle2 } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, Tooltip } from "recharts";
import { api, errMsg, sourceFor } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/AppShell";
import { ScoreBadge, SourceChip } from "@/components/Chips";
import { FixDiff } from "@/components/FixDiff";
import { ScoreFactors, ShieldPanel, VersionHistory } from "@/components/ListingPanels";
import { Button } from "@/components/ui/button";

const GEN_ERROR = "Couldn't generate a fix — try again.";

const Current = ({ l }) => (
  <section data-testid="current-listing" className="rounded-2xl border glass p-5 sm:p-6 space-y-5">
    <h2 className="text-xl font-medium">Current listing</h2>
    <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Title · <span className="font-mono">{l.title.length}/140</span></p><p data-testid="current-title" className="mt-1">{l.title}</p></div>
    <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Tags · <span className="font-mono">{l.tags.length}/13</span></p>
      <div data-testid="current-tags" className="mt-2 flex flex-wrap gap-1.5">{l.tags.map((t, i) => <span key={i} className="rounded-full border px-2.5 py-0.5 text-xs">{t}</span>)}</div></div>
    <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Attributes</p>
      <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">{Object.entries(l.attributes).map(([k, v]) => <p key={k}><span className="text-muted-foreground">{k}:</span> {v || "—"}</p>)}</div></div>
    <div><p className="text-xs uppercase tracking-wider text-muted-foreground">Description</p><p data-testid="current-description" className="mt-1 whitespace-pre-line text-sm text-foreground/80">{l.description}</p></div>
  </section>
);

const Trend = ({ daily, source }) => (
  <section data-testid="listing-stats" className="rounded-2xl border glass p-5 sm:p-6">
    <div className="flex items-center justify-between"><h2 className="text-xl font-medium">Daily views</h2><SourceChip label={source} testid="listing-stats-source" /></div>
    <div className="mt-3 h-32">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={daily}>
          <XAxis dataKey="d" hide />
          <Tooltip contentStyle={{ borderRadius: 12, fontSize: 12, background: "#0E1020", border: "1px solid rgba(255,255,255,0.1)", color: "#F4F1EA" }} />
          <Area type="monotone" dataKey="v" name="Views" stroke="#5EE6D0" fill="rgba(94,230,208,0.12)" strokeWidth={2} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
    <p className="mt-2 font-mono text-xs text-muted-foreground">30d: {daily.reduce((a, d) => a + d.v, 0)} views · {daily.reduce((a, d) => a + d.f, 0)} favorites</p>
  </section>
);

export default function ListingDetail() {
  const { id } = useParams();
  const { shop } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [gen, setGen] = useState(false);
  const [genError, setGenError] = useState("");
  const load = useCallback(() => api.get(`/listings/${id}`).then((r) => setData(r.data)).catch((e) => setError(errMsg(e))), [id]);
  useEffect(() => { load(); }, [load]);

  const [keywords, setKeywords] = useState("");
  const generate = async () => {
    setGen(true);
    setGenError("");
    try { await api.post(`/listings/${id}/generate`, { keywords }); await load(); }
    catch (e) { setGenError(GEN_ERROR); toast.error(GEN_ERROR); }
    finally { setGen(false); }
  };
  const act = (fn, msg) => async () => { try { await fn(); toast.success(msg); await load(); } catch (e) { toast.error(errMsg(e)); } };
  const undo = act(() => api.post(`/listings/${id}/undo`), "Restored the previous version.");
  const unlock = act(() => api.post(`/listings/${id}/unlock`, { confirm: true }), "Shield unlocked for this listing.");
  const lock = act(() => api.post(`/listings/${id}/lock`), "Bestseller Shield is back on.");
  const discard = act(() => api.delete(`/proposals/${data.proposal.id}`), "Proposal discarded. Nothing was changed.");

  if (error) return <AppShell><p data-testid="listing-error" className="text-muted-foreground">{error}</p></AppShell>;
  if (!data) return <AppShell><div className="h-96 rounded-2xl shimmer" /></AppShell>;
  const l = data.listing;
  return (
    <AppShell>
      <Link to="/app" data-testid="back-to-catalog" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" />Catalog</Link>
      <div className="mt-4 mb-8 flex flex-col gap-4 md:flex-row md:items-start md:justify-between fade-up">
        <div className="min-w-0">
          <p className="font-mono text-xs text-muted-foreground">Listing #{l.external_id} · {l.category}</p>
          <h1 data-testid="listing-title" className="mt-2 text-2xl sm:text-3xl font-medium break-words">{l.title}</h1>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {l.status === "updated" && (
              <span data-testid="listing-updated-status" className="inline-flex items-center gap-1 rounded-full bg-emerald-400/10 border border-emerald-300/25 px-3 py-1 text-xs text-emerald-300">
                <CheckCircle2 className="h-3.5 w-3.5" />Updated · {format(new Date(l.updated_at), "MMM d, yyyy HH:mm")}
              </span>
            )}
            {l.status === "restored" && <span data-testid="listing-restored-status" className="rounded-full border px-3 py-1 text-xs">Restored · {format(new Date(l.updated_at), "MMM d, HH:mm")}</span>}
            {l.shielded && <span data-testid="protected-bestseller-label" className="rounded-full border border-[#E9C98B]/30 bg-[#E9C98B]/[0.07] px-3 py-1 text-xs text-[#E9C98B]">Protected bestseller</span>}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right"><p className="text-xs uppercase tracking-wider text-muted-foreground">Health</p><SourceChip label="Estimate" testid="listing-score-source" /></div>
          <ScoreBadge score={l.score} size="lg" testid="listing-score-badge" />
        </div>
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-6 min-w-0">
          {data.proposal ? (
            <FixDiff proposal={data.proposal} options={l.attribute_options} shielded={l.shielded} onPushed={load} onDiscard={discard} />
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-dashed border-primary/40 bg-accent/40 p-5">
                <div className="flex-1 min-w-[200px]">
                  <p className="font-display text-lg">Generate an AI fix</p>
                  <p className="text-sm text-muted-foreground">New title, 13 tags, attributes and description, with a reason for each change. You review before anything is pushed.</p>
                </div>
                <Button onClick={generate} disabled={gen} className="btn-aurora rounded-full" data-testid="generate-fix-button">
                  <Sparkles className="h-4 w-4 mr-1" />{gen ? "Generating…" : "Generate fix"}
                </Button>
                {genError && <p data-testid="generate-error-message" className="w-full text-sm text-destructive">{genError}</p>}
                <label className="w-full text-sm">
                  <span className="text-muted-foreground">Your keywords (from eRank, Marmalead or your own research) — optional, comma or newline separated, up to 30 phrases.</span>
                  <textarea value={keywords} onChange={(e) => setKeywords(e.target.value)} rows={2} data-testid="listing-keywords-input"
                    placeholder="e.g. your top search phrases, comma separated"
                    className="mt-1.5 w-full rounded-xl border border-white/10 bg-background/60 px-4 py-3 text-sm outline-none focus:border-[#E9C98B]/60" />
                </label>
              </div>
              {gen && (
                <div data-testid="generate-loading" className="glass hairline-gold relative overflow-hidden rounded-2xl border p-6">
                  <div className="absolute inset-0 shimmer opacity-60" />
                  <p className="relative flex items-center gap-2 text-sm"><Sparkles className="h-4 w-4 text-[#E9C98B] icon-float" />Claude Opus 5.5 is writing your fix and checking it against Etsy's rules. This usually takes 15–40 seconds.</p>
                </div>
              )}
            </>
          )}
          <Current l={l} />
        </div>
        <aside className="space-y-6">
          <ShieldPanel listing={l} onUnlock={unlock} onLock={lock} />
          {data.can_undo && (
            <Button variant="outline" onClick={undo} className="w-full rounded-full h-11" data-testid="undo-button">
              <Undo2 className="h-4 w-4 mr-1" />Undo last push
            </Button>
          )}
          <ScoreFactors factors={l.factors} />
          <Trend daily={l.daily} source={sourceFor(shop)} />
          <VersionHistory versions={data.versions} />
        </aside>
      </div>
    </AppShell>
  );
}
